import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getRestaurants, updateRestaurant } from '@/lib/store';
import type { Restaurant } from '@/types';
import { getCurrentPosition, geocodeText, haversine, formatDistance } from '@/lib/geo';
import { MapPin, Loader2, Navigation, LocateFixed } from 'lucide-react';
import { buildAppleMapsLink } from '@/lib/maps';
import { compositeScore20 } from '@/lib/rating';

function parseDate(input?: string): number | null {
  if (!input) return null;
  const t = Date.parse(input.replace(/年|月/g, '/').replace('日', ''));
  return isNaN(t) ? null : t;
}

export default function NearbySuggest() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Array<Restaurant & { distance: number; driveMins: number }>>([]);

  function estimateDriveMinutes(distanceMeters: number): number {
    const km = distanceMeters / 1000;
    const avgKmh = 30; // 都市部の平均走行速度の目安
    return (km / avgKmh) * 60;
  }

  async function ensureLatLon(r: Restaurant): Promise<{ lat?: number; lon?: number }> {
    if (typeof r.lat === 'number' && typeof r.lon === 'number') return { lat: r.lat, lon: r.lon };
    const q = [r.name, r.location, r.municipality, r.prefecture].filter(Boolean).join(' ');
    const geo = await geocodeText(q);
    if (geo) {
      try { updateRestaurant(r.id, { lat: geo.lat, lon: geo.lon }); } catch {}
      return geo;
    }
    return {};
  }

  async function run() {
    setOpen(true);
    setLoading(true);
    setError(null);
    try {
      const me = await getCurrentPosition();
      const list = getRestaurants();
      // 最近訪れていない（60日以上）を優先
      const now = Date.now();
      const freshThreshold = 60 * 24 * 60 * 60 * 1000; // 60 days

      // まず座標があるものを粗選別
      const enriched: Array<Restaurant & { distance: number; driveMins: number }> = [];
      for (const r of list) {
        let lat = r.lat, lon = r.lon;
        if (typeof lat !== 'number' || typeof lon !== 'number') {
          const geo = await ensureLatLon(r);
          lat = geo.lat; lon = geo.lon;
        }
        if (typeof lat !== 'number' || typeof lon !== 'number') continue;
        const d = haversine({ lat, lon }, me);
        const mins = estimateDriveMinutes(d);
        enriched.push({ ...r, distance: d, driveMins: mins });
      }

      // フィルタ: 最近訪れていない or そもそも未訪問（visitDateなし）
      const filtered = enriched.filter(r => {
        const t = parseDate(r.visitDate) ?? null;
        const notRecentlyVisited = !t || (now - t) >= freshThreshold;
        const within1hByCar = r.driveMins <= 60;
        return within1hByCar && notRecentlyVisited;
      });

      // 近い順、同距離なら総合点/部分点で降順
      const score = (r: Restaurant) => compositeScore20(r);
      filtered.sort((a,b) => {
        const aUnvisited = !a.visitDate;
        const bUnvisited = !b.visitDate;
        if (aUnvisited !== bUnvisited) return aUnvisited ? -1 : 1;
        const byDist = a.distance - b.distance;
        if (byDist !== 0) return byDist;
        return (score(b) - score(a));
      });

      setResults(filtered);
    } catch (e: any) {
      setError(e?.message || '位置情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Button size="sm" className="bg-gradient-accent" onClick={run}>
        <LocateFixed className="h-4 w-4 mr-1" /> 今いる場所からのおすすめ
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>近場のおすすめ</DialogTitle>
          </DialogHeader>
          {loading ? (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> 計算中...
            </div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : results.length === 0 ? (
            <div className="text-sm text-muted-foreground">近くで条件に合う候補が見つかりませんでした。</div>
          ) : (
            <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1">
              {results.map(r => (
                <div key={r.id} className="flex items-start justify-between gap-3 rounded-md border p-3">
                  <div className="min-w-0">
                    <div className="font-medium truncate max-w-[16rem]">{r.name}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      <span className="truncate max-w-[14rem]">{r.location}</span>
                      <span className="ml-2">{formatDistance(r.distance)} / 約{Math.round(r.driveMins)}分</span>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      最終訪問: {r.visitDate ? r.visitDate : '未訪問'}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => window.open(buildAppleMapsLink(r.name, r.location, (r as any).lat, (r as any).lon, (r as any).prefecture, (r as any).municipality), '_blank')}
                    >
                      <Navigation className="h-4 w-4 mr-1" /> ナビ
                    </Button>
                    <a href={`/view/${r.id}`}>
                      <Button size="sm" variant="outline">表示</Button>
                    </a>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}


